import { useMemo } from "react";
import { SenderAvatar } from "@/features/shared/SenderAvatar";
import { useMailContactsOptional } from "@/hooks/useMailContacts";
import { mailContactAvatarLabel } from "@/lib/mail/contactLabels";
import { extractEmailAddress } from "@/lib/compose/composeDraft";
import { cn } from "@/lib/cn";

type Props = {
  label: string;
  fromRaw?: string;
  email?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
};

const SIZE_CLASS = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
} as const;

export function ContactAvatar({ label, fromRaw, email, size = "md", className }: Props) {
  const contacts = useMailContactsOptional();
  const resolvedEmail = useMemo(() => {
    if (email?.trim()) return email.trim().toLowerCase();
    if (fromRaw?.trim()) return extractEmailAddress(fromRaw).toLowerCase();
    return "";
  }, [email, fromRaw]);

  const contact = resolvedEmail && contacts ? contacts.getByEmail(resolvedEmail) : undefined;
  const photoBlob =
    contact && contact.status === "book" && contacts ? contacts.getPhotoBlob(contact) : null;
  const avatarLabel = contact ? mailContactAvatarLabel(contact) : label;

  if (photoBlob) {
    return (
      <img
        src={photoBlob}
        alt=""
        className={cn(
          "inline-flex shrink-0 rounded-full object-cover shadow-sm",
          SIZE_CLASS[size],
          className
        )}
      />
    );
  }

  return <SenderAvatar label={avatarLabel} size={size} className={className} />;
}
